import React, { createContext, ReactNode, useContext, useState } from 'react';
import { useAuth } from '@hooks/useAuth';
import { createAssetsUppy } from '@services/assets-uppy';
import Uppy from '@uppy/core';
import { registerServiceWorker } from 'src/register-sw';

interface IAssetsUppyContext {
  readonly uppy: Uppy;
  readonly userId: string;
}

const AssetsUppyContext = createContext<IAssetsUppyContext | undefined>(
  undefined
);

function createUppy(userId: string, getAccessToken: () => Promise<string>) {
  const result = createAssetsUppy(userId, getAccessToken);
  registerServiceWorker().catch(() => {
    result.uppy.emit('restore-canceled');
  });
  result.uppy.once('restored' as any, () => {
    const ghosts = result.uppy.getFiles().filter((x) => (x as any).isGhost);
    ghosts.forEach((x) => result.uppy.removeFile(x.id));
    result.uppy.emit('restore-confirmed');
  });
  return result;
}

export function AssetsUppyProvider({ children }: { children: ReactNode }) {
  const { user, getAccessToken } = useAuth(true);
  const [ctx, setCtx] = useState(() => createUppy(user.id, getAccessToken));
  if (user.id !== ctx.userId) {
    ctx.uppy.destroy();
    setCtx(createUppy(user.id, getAccessToken));
  }
  return (
    <AssetsUppyContext.Provider value={ctx}>
      {children}
    </AssetsUppyContext.Provider>
  );
}

export function useAssetsUppy() {
  const ctx = useContext(AssetsUppyContext);
  if (!ctx)
    throw new Error('No assets uppy found. Forgot to use AssetsUppyProvider?');
  return ctx.uppy;
}
